import { useIsCompact } from '../lib/useMediaQuery';

/**
 * SearchTrigger — the visible entry point to the global command-bar search.
 *
 * The command palette (organisms/CommandBar) is mounted once in the operator
 * shell and has always been openable by ⌘K / Ctrl-K / "/", but a keyboard
 * shortcut is undiscoverable in a live demo. This atom renders a faux search
 * field in the top bar; clicking it dispatches a `commandbar:open` CustomEvent
 * that the CommandBar listens for — zero shared state, no prop threading, no
 * lifting the palette's open-state out of the organism.
 *
 * Desktop: a full faux field (magnifier + muted "Search…" + ⌘K/Ctrl-K hint) so
 * the affordance reads clearly in a demo. Compact (< lg): an icon-only button,
 * to fit the collapsed top bar.
 *
 * Tokens only (no CSS classes), matching the v2 inline-style design system.
 */

/** Fire the event the mounted CommandBar listens for. */
function openCommandBar() {
  document.dispatchEvent(new CustomEvent('commandbar:open'));
}

// Best-effort platform sniff so the hint shows the right modifier. Falls back to
// Ctrl (the more common case) when the platform can't be read.
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');

function MagnifierIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-ink-3)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function SearchTrigger() {
  const isCompact = useIsCompact();
  const shortcut = IS_MAC ? '⌘K' : 'Ctrl K';
  const keyshortcuts = IS_MAC ? 'Meta+K' : 'Control+K';

  const common = {
    type: 'button' as const,
    onClick: openCommandBar,
    'data-testid': 'global-search-trigger',
    'aria-label': 'Search clients, parcels, properties',
    'aria-keyshortcuts': keyshortcuts,
  };

  if (isCompact) {
    return (
      <button
        {...common}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 'var(--r-md)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-ink-3)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <MagnifierIcon />
      </button>
    );
  }

  return (
    <button
      {...common}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        minWidth: 200,
        height: 36,
        padding: '0 var(--sp-2) 0 var(--sp-3)',
        borderRadius: 'var(--r-md)',
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        flexShrink: 0,
      }}
    >
      <MagnifierIcon />
      <span style={{ flex: 1, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Search…</span>
      <kbd
        style={{
          fontSize: 'var(--fs-xs)',
          color: 'var(--color-ink-3)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-sm, 4px)',
          padding: '1px 6px',
        }}
      >
        {shortcut}
      </kbd>
    </button>
  );
}
